// PoolScreener data — Uniswap-v3 subgraph, used ONLY for the reliable feeTier.
// The subgraph's totalValueLockedUSD is unreliable on Base (it derives USD from
// its own price tracking and can be wildly wrong — it reported a dead ~$771 pool
// as $8M), so we do NOT use it for TVL. GeckoTerminal's on-chain reserves are
// authoritative for TVL/volume; this only fills in the fee tier for the exact
// pool GeckoTerminal resolved, matched by address (no TVL guessing).
import { z } from 'zod';
import { cached } from './cache';
import { API_BASE } from '../../api';
import type { PoolUniverseItem } from './types';

// Only the two fields we trust; extra fields from the endpoint are stripped.
const SubgraphPoolSchema = z.object({
  id: z.string(),
  feeTier: z.string(),
});
export type SubgraphPool = z.infer<typeof SubgraphPoolSchema>;

// The bot normalizes the gateway response to { pools: [...] }.
const SubgraphResponseSchema = z.object({
  pools: z.array(SubgraphPoolSchema).default([]),
});

/** Pure: the feeTier (fraction) of the pool matching `poolAddress`, or null.
 *  Matched by address — GeckoTerminal already resolved the exact pool, so there
 *  is no TVL guessing. Subgraph reports hundredths-of-a-bip (500 → 0.0005).
 *  Exposed for tests. */
export function feeTierForPool(pools: SubgraphPool[], poolAddress: string): number | null {
  const want = poolAddress.toLowerCase();
  const hit = pools.find((p) => p.id.toLowerCase() === want);
  if (!hit) return null;
  const tier = Number(hit.feeTier) / 1e6;
  return Number.isFinite(tier) && tier > 0 ? tier : null;
}

/** Fetch the exact-pool feeTier from the bot's GET endpoint (cached 1h). Non-
 *  Uniswap venues, no resolved address, a missing key (503), or any error → null
 *  (GeckoTerminal's fee, if any, then a derived fee, take over). */
export async function fetchSubgraphFeeTier(
  pool: PoolUniverseItem,
  poolAddress: string,
): Promise<number | null> {
  if (pool.project !== 'uniswap-v3') return null;
  const tokens = pool.underlyingTokens;
  if (!tokens || tokens.length < 2 || !poolAddress) return null;
  const lower = tokens.map((t) => t.toLowerCase());
  try {
    return await cached(`subgraph:fee:${poolAddress.toLowerCase()}`, async () => {
      // Compose against API_BASE (the api domain) + send the auth cookie, like
      // the shared client — a relative URL would hit the frontend domain.
      const res = await fetch(
        `${API_BASE}/screener/uniswap-pool?tokens=${encodeURIComponent(lower.join(','))}`,
        { credentials: 'include' },
      );
      if (!res.ok) return null; // 503 unconfigured / 5xx
      const parsed = SubgraphResponseSchema.parse(await res.json());
      return feeTierForPool(parsed.pools, poolAddress);
    });
  } catch {
    return null;
  }
}
